import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Button,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

interface EmergencyVisitRecord {
  id: string;
  patientName: string;
  age: number;
  gender: string;
  arrival: string;
  complaint: string;
  status: "critical" | "moderate" | "stable";
}

const EmergencyVisit = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");

  const emergencyVisits: EmergencyVisitRecord[] = [
    {
      id: "ER-001",
      patientName: "Jane Cooper",
      age: 54,
      gender: "Female",
      arrival: "08-28-2025 11:00 AM",
      complaint: "Severe chest pain, difficulty breathing",
      status: "critical",
    },
  ];

  const filteredData = emergencyVisits.filter((record) => {
    const matchesSearch =
      record.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.patientName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "All Status" || record.status === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "critical":
        return "#EE0F12";
      case "moderate":
        return "#F6AD55";
      case "stable":
        return "#1FB551";
      default:
        return "#666";
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#f9f9f9",
        minHeight: "100vh",
        fontFamily: "Quicksand, sans-serif",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px",
        }}
      >
        <h1 style={{ fontSize: "28px", fontWeight: "600", margin: 0, color: "#333" }}>
          Emergency Visit
        </h1>
        <div style={{ display: "flex", gap: "12px" }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              color: "#009FF5",
              borderColor: "#e2e8f0",
              "&:hover": { borderColor: "#009FF5", backgroundColor: "#f0f9ff" },
            }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: "14px",
              backgroundColor: "#009FF5",
              color: "#fff",
              "&:hover": { backgroundColor: "#0077b3" },
            }}
          >
            + Add Patient
          </Button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginBottom: "24px",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Search patient..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: "10px 14px",
            borderRadius: "6px",
            border: "1px solid #e2e8f0",
            fontSize: "14px",
            fontFamily: "Quicksand, sans-serif",
            backgroundColor: "#fff",
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "10px 14px",
            borderRadius: "6px",
            border: "1px solid #e2e8f0",
            fontSize: "14px",
            fontFamily: "Quicksand, sans-serif",
            backgroundColor: "#fff",
            minWidth: "140px",
            cursor: "pointer",
          }}
        >
          <option value="All Status">All Status</option>
          <option value="critical">Critical</option>
          <option value="moderate">Moderate</option>
          <option value="stable">Stable</option>
        </select>
      </div>

      {/* Table Section */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#f9f9f9",
                "& th": {
                  fontFamily: "Quicksand, sans-serif",
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "#666",
                  borderBottom: "1px solid #e2e8f0",
                  padding: "14px",
                },
              }}
            >
              <TableCell>ID</TableCell>
              <TableCell>Patient Name</TableCell>
              <TableCell>Demographics</TableCell>
              <TableCell>Arrival</TableCell>
              <TableCell>Complaint</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData.map((record) => (
                <TableRow
                  key={record.id}
                  sx={{
                    "&:hover": { backgroundColor: "#fafafa" },
                    "& td": {
                      fontFamily: "Quicksand, sans-serif",
                      fontSize: "14px",
                      color: "#333",
                      borderBottom: "1px solid #e2e8f0",
                      padding: "14px",
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: "500" }}>{record.id}</TableCell>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: "#009FF5",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {getInitials(record.patientName)}
                      </Avatar>
                      <span>{record.patientName}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: "12px", lineHeight: "1.5" }}>
                      <div>{record.age} years</div>
                      <div>{record.gender}</div>
                    </div>
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>{record.arrival}</TableCell>
                  <TableCell sx={{ maxWidth: "250px" }}>
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                      {record.complaint}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(record.status)}
                      sx={{
                        backgroundColor: getStatusColor(record.status),
                        color: "#fff",
                        fontFamily: "Quicksand, sans-serif",
                        fontWeight: "600",
                        fontSize: "12px",
                        height: "28px",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", padding: "30px" }}>
                  <div style={{ color: "#999", fontSize: "14px" }}>
                    No emergency visits found
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default EmergencyVisit;
